/**
 * Normalize amenity id collections from list/detail payloads
 * (array, Set, or missing) into a plain number[].
 */
export function toAmenityIdList(
    amenities: Iterable<number> | number[] | Set<number> | null | undefined
): number[] {
    if (!amenities) return [];
    const ids = Array.isArray(amenities)
        ? amenities
        : Array.from(amenities as Iterable<number>);
    return ids.filter((id): id is number => typeof id === 'number' && !Number.isNaN(id));
}

/**
 * Explore cards historically only read listing.amenities. Provider-backed
 * spaces (IWG/Regus) often carry amenities on the nested workspace instead,
 * matching how workspace detail pages resolve them. Prefer listing ids when
 * present; otherwise fall back to workspace ids.
 *
 * `preferWorkspace` inverts the precedence: a workspace card is backed by a
 * representative listing, so it should show the *workspace's* amenities first
 * (listing fallback) — otherwise the same workspace shows different amenities
 * depending on which listing the backend picked to represent it.
 */
export function resolveCardAmenityIds(
    listing: {
        amenities?: Iterable<number> | number[] | Set<number> | null;
        workspace?: {
            amenities?: Iterable<number> | number[] | Set<number> | null;
        } | null;
    },
    preferWorkspace = false
): number[] {
    const fromListing = toAmenityIdList(listing.amenities);
    const fromWorkspace = toAmenityIdList(listing.workspace?.amenities);
    if (preferWorkspace) {
        return fromWorkspace.length > 0 ? fromWorkspace : fromListing;
    }
    if (fromListing.length > 0) return fromListing;
    return fromWorkspace;
}

/** Parse amenity filter values (`"1,2"` / `["1","2"]`) into numeric ids. */
export function parseAmenityFilterIds(
    amenitiesIdIn?: string | string[] | null
): number[] {
    if (!amenitiesIdIn) return [];
    const raw = Array.isArray(amenitiesIdIn)
        ? amenitiesIdIn
        : String(amenitiesIdIn).split(',');
    return raw.map(Number).filter((id) => Number.isFinite(id));
}

/** True when listing matches selected amenities via listing OR workspace ids. */
export function listingMatchesAmenityFilter(
    listing: {
        amenities?: Iterable<number> | number[] | Set<number> | null;
        workspace?: {
            amenities?: Iterable<number> | number[] | Set<number> | null;
        } | null;
    },
    amenityIds: Array<string | number>
): boolean {
    const selected = new Set(
        amenityIds.map(Number).filter((id) => Number.isFinite(id))
    );
    if (!selected.size) return true;
    return resolveCardAmenityIds(listing).some((id) => selected.has(id));
}

/** Dedupe explore rows by workspace (distinct mode) or listing id. */
export function listingDedupeKey(
    listing: {
        id?: number;
        workspace?: { id?: number } | null;
    },
    distinctWorkspace = false
): string | number | null {
    if (distinctWorkspace) {
        return listing.workspace?.id ?? listing.id ?? null;
    }
    return listing.id ?? listing.workspace?.id ?? null;
}

/** Collapse listing rows to one per workspace. */
export function uniqueWorkspaceListings<
    T extends {
        id?: number;
        workspace?: { id?: number } | null;
    }
>(listings: T[]): T[] {
    const seen = new Set<string | number>();
    const unique: T[] = [];
    for (const listing of listings) {
        const key = listingDedupeKey(listing, true) ?? `row-${unique.length}`;
        if (seen.has(key)) continue;
        seen.add(key);
        unique.push(listing);
    }
    return unique;
}

type AmenityFilterItem = {
    id?: number;
    name?: string;
    url?: string | { toString(): string } | null;
};

/**
 * Keep the curated Hotdesk amenity filter list, and append amenity ids used by
 * Regus/IWG spaces in the current results (resolved from listing OR workspace).
 */
export function mergeRegusAmenitiesIntoFilterList<T extends AmenityFilterItem>(
    curated: T[],
    listings: Array<{
        amenities?: Iterable<number> | number[] | Set<number> | null;
        workspace?: {
            amenities?: Iterable<number> | number[] | Set<number> | null;
        } | null;
        metaData?: unknown;
        providerCode?: unknown;
        provider?: unknown;
    }>,
    amenitiesMap: Record<number, { id: number; name: string; url?: string }>,
    isProviderListing: (listing: unknown) => boolean
): T[] {
    const merged = [...curated];
    const seen = new Set(
        curated
            .map((a) => a.id)
            .filter((id): id is number => typeof id === 'number')
    );

    for (const listing of listings) {
        if (!isProviderListing(listing)) continue;
        for (const id of resolveCardAmenityIds(listing)) {
            if (seen.has(id)) continue;
            const item = amenitiesMap[id];
            if (!item?.name) continue;
            seen.add(id);
            merged.push({
                id: item.id,
                name: item.name,
                url: item.url
            } as T);
        }
    }

    return merged;
}
